import { useEffect } from "react";
import { toast } from "sonner";
import { emitCacheEvent } from "./cacheBus";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Subscribes to the backend SSE feed that broadcasts cache-invalidation events.
 * Shows a toast whenever Odoo master data changes (auto-detected every 5 min by
 * the backend worker) or when an admin manually clears the cache.
 *
 * Auto-reconnects with exponential backoff on disconnect.
 */
export function useCacheEvents(token) {
  useEffect(() => {
    if (!token) return undefined;
    let es = null;
    let reconnectTimer = null;
    let attempt = 0;
    let cancelled = false;

    const connect = () => {
      if (cancelled) return;
      const url = `${BACKEND_URL}/api/admin/cache-events?token=${encodeURIComponent(token)}`;
      es = new EventSource(url);

      es.addEventListener("open", () => {
        attempt = 0;
      });

      es.addEventListener("cache_invalidated", (e) => {
        try {
          const data = JSON.parse(e.data || "{}");
          const reason = data.reason === "manual_clear"
            ? `Cache cleared by ${data.by || "admin"}`
            : "Odoo data updated — fresh data loading";
          toast.success(reason, {
            description: `${data.cleared} cached datasets refreshed. This page is auto-refreshing.`,
            duration: 6000,
          });
          // Fan out to any page-level refresh subscribers (debounced).
          emitCacheEvent(data);
        } catch {/* ignore parse errors */}
      });

      es.addEventListener("error", () => {
        if (es) {
          es.close();
          es = null;
        }
        if (cancelled) return;
        attempt += 1;
        const delay = Math.min(30000, 1000 * 2 ** Math.min(attempt, 5)); // 2s,4s,8s,16s,32s cap
        reconnectTimer = setTimeout(connect, delay);
      });
    };

    connect();

    return () => {
      cancelled = true;
      if (reconnectTimer) clearTimeout(reconnectTimer);
      if (es) es.close();
    };
  }, [token]);
}
